import Service from '../service';
import Request from '../util/request';

export default class Auth {
    public verified: string | null = null;

    public timestamp: number = 0;
    public status: any = null;
    public loading: any = null;
    public session: any = {};

    private request: Request;
    private generation = 0;

    constructor(public service: Service) {
        this.request = new Request();
        window.addEventListener('nanofab-auth-required', () => this.invalidate());
    }

    public invalidate() {
        this.generation++;
        this.status = false;
        this.session = {};
        this.verified = null;
        this.timestamp = 0;
        this.loading = true;
        window.dispatchEvent(new CustomEvent('nanofab-auth-changed'));
    }

    public async init() {
        const generation = ++this.generation;
        const previous = JSON.stringify([this.status, this.session?.id, this.session?.chat_csrf]);
        try {
            const pathname = window.location.pathname;
            const authCheckUrl = pathname === '/agent' || pathname.startsWith('/agent/')
                ? '/agent/auth/check'
                : '/auth/check';
            const { code, data }: any = await this.request.post(authCheckUrl);
            if (generation !== this.generation) return this;
            const session = data?.session || {};
            const bandi = session.authenticated === true && session.auth_provider === 'bandi';
            const guest = session.auth_provider === 'guest' && session.authenticated === false
                && session.chat_allowed === true && session.role === 'guest'
                && /^guest:[0-9a-f]{64}$/.test(session.id || '');
            const authenticated = code === 200 && data?.status === true && (bandi || guest)
                && typeof session.id === 'string' && !!session.chat_csrf;
            this.session = authenticated ? session : {};
            this.status = authenticated;
            this.verified = authenticated ? session.verified : null;
        } catch (_) {
            if (generation !== this.generation) return this;
            this.status = false;
            this.session = {};
            this.verified = null;
        }
        this.timestamp = Date.now();
        this.loading = true;
        if (previous !== JSON.stringify([this.status, this.session?.id, this.session?.chat_csrf])) {
            window.dispatchEvent(new CustomEvent('nanofab-auth-changed'));
        }
        return this;
    }

    public async update() {
        while (this.loading === null) {
            await this.service.render(100);
        }

        let diff = new Date().getTime() - this.timestamp;
        if (diff > 1000 * 60) {
            this.loading = null;
            await this.init();
        }
    }

    public check: any = new Proxy(((root) => {
        let obj: any = () => {
            return this.status;
        }

        obj.root = root;
        return obj;
    })(this), {
        get(target, propKey) {
            let propValue: any = target.root.session[propKey];

            return (values: any = null) => {
                if (values === null) {
                    return true;
                }

                if (typeof values == 'boolean') {
                    if (values === propValue)
                        return true;
                    return false;
                }

                if (typeof values == 'string')
                    values = [values];

                if (values.indexOf(propValue) >= 0) {
                    return true;
                }

                return false;
            };
        }
    });

    public allow: any = new Proxy(((root) => {
        let obj: any = (redirect: any = null) => {
            if (root.check()) return true;
            if (redirect) location.href = redirect;
            return false;
        }

        obj.root = root;
        obj.check = root.check;
        return obj;
    })(this), {
        get(target, propKey) {
            return (values: any = null, redirect: any = null) => {
                if (target.check[propKey](values)) return true;
                if (redirect) location.href = redirect;
                return false;
            }
        }
    });

    public hash(password: string = '') {
        return this.service.crypto.SHA256(password).toString();
    }
}
